import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";

import ItemComponentIdentityContext from "src/contexts/data/ItemComponentIdentityContext";
import useItemComponentIdentityById from "src/hooks/data/load/useItemComponentIdentityById";

/**
 * Loads item component identity from the matching route parameter for descendants.
 *
 * Preconditions: Render within the router that supplies the route parameters.
 * Postconditions: An absent or empty parameter becomes undefined; otherwise base-10 parseInt
 * supplies the ID, accepting numeric prefixes and producing NaN for unparseable text.
 * Loading and errors remain in context; this wrapper does not gate children or enforce access.
 * Assumptions: The loader handles request state and the server enforces authorization.
 */
export const RouteItemComponentIdentityProvider: FC<PropsWithChildren> = ({ children }) => {
    const { itemComponentId } = useParams<{ itemComponentId: string }>();
    const context = useItemComponentIdentityById(
        itemComponentId ? Number.parseInt(itemComponentId, 10) : undefined
    );

    return (
        <ItemComponentIdentityContext.Provider value={context}>
            {children}
        </ItemComponentIdentityContext.Provider>
    );
};
